import React, {useEffect, useState } from 'react';
import {
    TextField,
    IconButton,
    InputAdornment,
    Button,
    FormControl,
    Box,
    Stack,
    Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import BaseDialog from '../../../BaseDialog';
import { useAppDispatch } from "../../../../hooks/hooks";
import { fetchAccessKey, removeAccessKey, upsertAccessKey } from "../../../../store/Vanlifer/Community/accessKeySlice";
import ConfirmDialog from "./ConfirmDialog";

type EditSharingLinkProps = {
    open: boolean;
    tourId: number;
    isPrivate: boolean;
    onClose: () => void;
    showSnackbar: (message: string, severity?: 'success' | 'error') => void;
};

const buildPrivateLink = (tourId: number, key: string | null) =>
    key
        ? `${window.location.origin}/vanlifer/Community/Tour/${tourId}?key=${key}`
        : null;

export default function EditSharingLinkDialog({
                                                  open,
                                                  tourId,
                                                  isPrivate,
                                                  onClose,
                                                  showSnackbar,
                                              }: EditSharingLinkProps) {
    const dispatch = useAppDispatch();
    const [privateLink, setPrivateLink] = useState<string | null>(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);

    useEffect(() => {
        if (open) {
            const loadAccessKey = async () => {
                const resultAction = await dispatch(fetchAccessKey(tourId));
                if (fetchAccessKey.fulfilled.match(resultAction)) {
                    const key = resultAction.payload;
                    setPrivateLink(buildPrivateLink(tourId, key));
                }
            };
            loadAccessKey();
        }
    }, [open, dispatch, tourId]);


    const handleCopy = async () => {
        if (privateLink) {
            await navigator.clipboard.writeText(privateLink);
            showSnackbar('Link kopiert', 'success');
        }
    };

    const handleDeleteConfirmed = async () => {
        const resultAction = await dispatch(removeAccessKey(tourId));
        if (removeAccessKey.fulfilled.match(resultAction)) {
            setPrivateLink(buildPrivateLink(tourId, null));
            showSnackbar('Link wurde entfernt', 'success');
        } else {
            showSnackbar('Fehler beim Entfernen des Links', 'error');
        }
        setConfirmDeleteOpen(false);
    };

    const handleRegenerateConfirmed = async () => {
        const resultAction = await dispatch(upsertAccessKey(tourId));
        if (upsertAccessKey.fulfilled.match(resultAction)) {
            const newKey = resultAction.payload;
            setPrivateLink(buildPrivateLink(tourId, newKey));
            showSnackbar('Neuer Link generiert', 'success');
        } else {
            showSnackbar('Fehler beim Generieren des Links', 'error');
        }
        setConfirmRegenerateOpen(false);
    };


    return (
        <>
            <BaseDialog
                open={open}
                onClose={onClose}
                disableSave={false}
                title="Zugangslink"
                closeOnly={true}
            >
                <Box width="100%">
                    <Typography variant="body2">
                        Mit diesem Link können deine Freunde deine Reise und auch deine privaten Stories sehen.
                        Wenn du einen neuen Link erstellst oder diesen löschst, wird der bisherige Link sofort ungültig.
                    </Typography>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            fullWidth
                            label="Privater Zugangslink"
                            value={privateLink ?? ''}
                            disabled
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Link kopieren">
                                            <span>
                                                <IconButton
                                                    onClick={handleCopy}
                                                    disabled={!privateLink}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </FormControl>

                    <Stack direction="row" spacing={2} mt={1}>
                        <Tooltip title="Erstellt einen neuen Link und deaktiviert den alten.">
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    if (privateLink) {
                                        setConfirmRegenerateOpen(true);
                                    } else {
                                        handleRegenerateConfirmed();
                                    }
                                }}
                                startIcon={<RefreshIcon />}
                            >
                                Generieren
                            </Button>
                        </Tooltip>
                        <Tooltip title="Entfernt den Link dauerhaft.">
                          <span>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setConfirmDeleteOpen(true)}
                                disabled={!privateLink}
                                startIcon={<DeleteIcon />}
                            >
                              Entfernen
                            </Button>
                          </span>
                        </Tooltip>
                    </Stack>

                </Box>
            </BaseDialog>


        {/* Confirm Dialog: Entfernen */}
        <ConfirmDialog
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={handleDeleteConfirmed}
            confirmText="Entfernen"
            cancelText="Abbrechen"
            confirmColor="error"
            title="Zugangslink entfernen?"
            content="Der bestehende Zugangslink wird dauerhaft gelöscht. Nutzer, die den alten Link haben, verlieren den Zugriff auf deine Reise und privaten Stories. Möchtest du wirklich fortfahren?"
            icon={<DeleteIcon color="error" />}
        />

        {/* Confirm Dialog: Neu generieren */}
        <ConfirmDialog
            open={confirmRegenerateOpen}
            onClose={() => setConfirmRegenerateOpen(false)}
            onConfirm={handleRegenerateConfirmed}
            confirmText="Generieren"
            cancelText="Abbrechen"
            confirmColor="warning"
            title="Neuen Link generieren?"
            content="Der aktuelle Link wird dadurch deaktiviert. Nutzer mit dem alten Link verlieren den Zugriff. Möchtest du wirklich einen neuen Link erstellen?"
            icon={<RefreshIcon color="warning" />}
        />
    </>
    );
};
